<template>
    <div class="loyalty-intro">
        <SeoStructuredDataContent component="SeoStructuredDataFaq" :data="faq"></SeoStructuredDataContent>
        <LoyaltyClouds></LoyaltyClouds>
        <div class="islands">
            <UIImage
                :resource="true"
                lazy
                :src="`images/loyalty/island-payment-card.png`"
                class="payment-island d-none d-md-block"
                fit="contain"
                :alt="'Island payment card'"
                :responsive-widths="[160, 220, 255]"
                sizes="(max-width: 768px) 160px, (max-width: 1024px) 220px, 255px"
            />
            <UIImage
                :resource="true"
                lazy
                :src="`images/loyalty/island-gift-card.png`"
                class="giftcard-island"
                fit="contain"
                :alt="'Island giftcard'"
                :responsive-widths="[160, 220, 255]"
                sizes="(max-width: 768px) 160px, (max-width: 1024px) 220px, 240px"
            />
            <UIImage
                :resource="true"
                lazy
                :src="`images/loyalty/island-phone-credit.png`"
                class="phone-island d-none d-md-block"
                fit="contain"
                :alt="'Island giftcard'"
                :responsive-widths="[160, 220, 264]"
                sizes="(max-width: 768px) 160px, 264px"
            />
            <UIImage
                :resource="true"
                lazy
                :src="`images/loyalty/island-gaming-card.png`"
                class="gaming-island"
                fit="contain"
                :alt="'Game Island'"
                :responsive-widths="[160, 220, 274]"
                sizes="(max-width: 768px) 160px, 274px"
            />
            <div class="bg-island-left"><nuxt-resource src="images/loyalty/island-7.svg" alt="Main island left" /></div>
            <div class="bg-island-right">
                <nuxt-resource src="images/loyalty/island-7.svg" alt="Main island right" />
            </div>
        </div>
        <UIGridContainer class="dundle-world">
            <UIGridRow>
                <UIGridCol md="6" offset-md="3">
                    <UIImage
                        :resource="true"
                        lazy
                        :src="`images/loyalty/logo-dundle-world.png`"
                        class="logo img-fluid"
                        fit="cover"
                        :alt="'Dundle world logo'"
                        :responsive-widths="[340, 482, 680, 720, 960]"
                        sizes="(max-width: 768px) 100%, 50%"
                    />
                    <div class="info">
                        <div class="info-sign">
                            <div class="info-sign-container">
                                <h1 class="mt-0">{{ translate('title') }}</h1>
                                <div class="font-weight-bold">{{ translate('subtitle') }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="info-chain-sign">
                        <div class="info-sign">
                            <div class="info-sign-container">
                                <h2 class="mt-0">{{ translate('introTitle') }}</h2>
                                <div class="intro" v-html="translate('introText')"></div>
                            </div>
                        </div>
                    </div>
                    <div class="mouse-icon mt-4 mx-auto" @click="scrollDown">
                        <nuxt-resource src="images/loyalty/intro/mouse_01.svg" alt="Scroll down" />
                        <UIIcon :name="['fas', 'chevron-down']" />
                    </div>
                </UIGridCol>
            </UIGridRow>
        </UIGridContainer>
        <div ref="introMountains" class="intro-mountains">
            <UIGridContainer>
                <nuxt-resource
                    lazy
                    src="images/loyalty/intro/intro-mountains.svg"
                    class="intro-mountain"
                    alt="Mountains"
                />
            </UIGridContainer>
            <div class="grass-bg">
                <UIGridContainer>
                    <LoyaltyBanner :title="translate('howBanner')" />
                    <UIGridRow class="free-products">
                        <UIGridCol v-for="step in 3" :key="step" md="4" class="mb-5">
                            <nuxt-resource
                                lazy
                                class="mb-2"
                                :src="`images/loyalty/intro/intro-step${step}.svg`"
                                :alt="`Step ${step}`"
                            />
                            <h4 class="title">{{ translate(`step${step}Title`) }}</h4>
                            <div class="text" v-html="translate(`step${step}Text`)"></div>
                        </UIGridCol>
                    </UIGridRow>
                </UIGridContainer>
                <nuxt-resource lazy src="images/loyalty/intro/money-tree.svg" class="money-tree" alt="Money tree" />
                <nuxt-resource
                    lazy
                    src="images/loyalty/intro/tree-cash-money.svg"
                    class="cash-tree d-none d-md-block"
                    alt="Cash money"
                />
                <nuxt-resource
                    lazy
                    src="images/loyalty/intro/tresure-tree.svg"
                    class="treasure-tree d-none d-md-block"
                    alt="Treasure tree"
                />
                <nuxt-resource lazy src="images/loyalty/intro/bush.svg" class="bush" alt="Bush" />
            </div>
            <div class="faq">
                <UIGridContainer>
                    <LoyaltyBanner :title="translate('faq')" />
                    <UIGridRow>
                        <UIGridCol md="8" offset-md="2">
                            <UIAccordion v-if="faq.length" class="faq-accordion" :items="faq">
                                <template #header="{ item }">
                                    {{ item.translations[0].title }}
                                </template>
                                <template #default="{ item }">
                                    <div v-html="translateGlobalParams(item.translations[0].answer)"></div>
                                </template>
                            </UIAccordion>
                            <div class="start-shopping">
                                <div class="shopping-text">{{ translate(`startShoppingText`) }}</div>
                                <UIButton class="start-shopping-button" variant="loyalty" :to="$locale.path('index')">{{
                                    translate(`button.startShopping`)
                                }}</UIButton>
                            </div>
                        </UIGridCol>
                    </UIGridRow>
                </UIGridContainer>
            </div>
            <div class="left-cloud d-none d-md-block">
                <nuxt-resource lazy src="images/loyalty/cloud-1.svg" alt="Cloud" aria-hidden="true" />
            </div>
            <div class="middle-cloud d-none d-md-block">
                <nuxt-resource lazy src="images/loyalty/cloud-2.svg" alt="Cloud" aria-hidden="true" />
            </div>
            <div class="right-cloud d-none d-md-block">
                <nuxt-resource lazy src="images/loyalty/cloud-1.svg" alt="Cloud" aria-hidden="true" />
            </div>
        </div>
    </div>
</template>

<script>
import { UIGridContainer, UIGridRow, UIGridCol, UIAccordion, UIButton, UIIcon, UIImage } from '@dundle/ui/components';
import { LoyaltyClouds, LoyaltyBanner } from '~/components/loyalty';
import SeoStructuredDataContent from '~/components/seo/SeoStructuredDataContent';
import TranslationMixin from '~/mixins/TranslationMixin';
import PageMixin from '~/mixins/PageMixin';

export default {
    components: {
        SeoStructuredDataContent,
        LoyaltyClouds,
        LoyaltyBanner,
        UIGridContainer,
        UIGridRow,
        UIGridCol,
        UIAccordion,
        UIButton,
        UIIcon,
        UIImage,
    },

    mixins: [TranslationMixin, PageMixin],

    async asyncData({ app, route }) {
        const subcategory = await app.$api.faq.getCategoryBySlug('dundle-world-help');
        const FAQByTitle = await app.$api.faq.getCategoryQuestions(subcategory.name);
        const pageData = await PageMixin.fetchData({ app, route });
        return {
            ...pageData,
            subcategory,
            faq: FAQByTitle,
        };
    },
    mounted() {
        this.$gtm?.push({ event: 'page_view_reward_info' });
    },

    methods: {
        scrollDown() {
            const topPos = this.$refs.introMountains.offsetTop;
            window.scrollTo({ top: topPos, behavior: 'smooth' });
        },
    },
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

.loyalty-intro {
    position: relative;
    overflow: hidden;
    margin-bottom: -1.5rem;

    @media screen and (min-width: 768px) {
        margin-top: -2rem;
        margin-bottom: -3.5rem;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 500px;
        background: linear-gradient(to bottom, #def8ff 0%, #fdf8eb 48%, #ffffff 100%);
        z-index: -1;

        @media screen and (max-width: 767px) {
            height: 1200px;
        }
    }
}

.islands {
    & > img,
    & > svg {
        position: absolute;
        width: 255px;

        @media screen and (max-width: 991px) {
            width: 220px;
        }

        @media screen and (max-width: 767px) {
            width: 160px;
        }
    }

    & > img {
        animation: float 15s ease-in-out infinite;
    }

    .gaming-island {
        @media screen and (min-width: 768px) {
            top: 16rem;
            left: calc(50% + 254px);
            right: initial;
            width: 274px;
        }

        top: 5rem;
        right: -2rem;
        width: 174px;
        animation: float2 15s ease-in-out infinite;
    }

    .payment-island {
        top: 41rem;
        left: calc(50% + 324px);

        @media screen and (min-width: 768px) and (max-width: 991px) {
            left: calc(50% + 235px);
            z-index: -1;
        }
    }

    .giftcard-island {
        @media screen and (min-width: 991px) {
            width: 241px;
        }

        @media screen and (min-width: 768px) {
            left: calc(50% - (255px + 235px));
            top: 2.5rem;
        }

        left: 1.2rem;
        top: 0;
    }

    .phone-island {
        @media screen and (min-width: 991px) {
            left: calc(50% - (255px + 264px));
        }

        @media screen and (min-width: 768px) and (max-width: 991px) {
            left: calc(50% - (255px + 264px));
            z-index: -1;
            width: 264px;
        }

        @media screen and (min-width: 768px) {
            top: 38rem;
        }

        left: 1.4rem;
        animation: float2 15s ease-in-out infinite;
    }

    .bg-island-left {
        position: absolute;
        width: 75px;
        top: 310px;
        left: calc(50% - 750px);
        transform: rotateY(180deg);
    }

    .bg-island-right {
        position: absolute;
        width: 100px;
        top: 550px;
        left: calc(50% + 550px);
    }
}

.dundle-world {
    padding-top: 14rem;

    @media screen and (min-width: 768px) {
        padding-top: 6rem;
    }

    .logo {
        width: 100%;
        margin: 0 auto -4rem auto;

        // aspect-ratio not supported on iOS < 15 and Samsung Internet < 15
        aspect-ratio: math.div(340, 263); // Prevent CLS
    }

    .mouse-icon {
        color: #9c6049;
        width: 1.2rem;
        text-align: center;
        cursor: pointer;

        &:hover svg {
            animation: bounce 2s infinite;
        }
    }
}

.info {
    z-index: 1;
    position: relative;
}

.info-chain-sign {
    .info-sign {
        margin-top: 1rem;

        &::before {
            content: '';
            background: url('https://images.dundle.com/resources/images/loyalty/intro/chain.svg') top center;
            background-size: 100% 100%;
            width: 100%;
            height: 4rem;
            position: absolute;
            top: -2.1rem;
            left: 0;
        }

        padding: 2rem 0;
    }
}

.info-sign {
    background: url('https://images.dundle.com/resources/images/loyalty/intro-step-1-top.svg'),
        url('https://images.dundle.com/resources/images/loyalty/intro-step-1-bottom.svg');
    background-size: 100%, 100%;
    background-position: top center, bottom center;
    background-repeat: no-repeat, no-repeat;
    color: $color-grey-7;
    text-align: center;
    position: relative;
    padding: 1.5rem 0;

    @media screen and (min-width: 768px) {
        padding: 2.66rem 0;
    }

    .info-sign-container {
        background: url('https://images.dundle.com/resources/images/loyalty/intro-step-1-middle.svg') repeat-y;
        background-size: 100%;
        padding: 0 2rem 1rem 2rem;

        @media screen and (max-width: 767px) {
            padding-bottom: 1rem;
        }
    }

    h1,
    h2 {
        font-size: 1.6rem;
        color: $color-grey-7;
    }

    .intro {
        line-height: 1.6rem;
    }
}

.header-banner {
    margin: 0 auto 3.3rem auto !important;
    display: block !important;
    width: fit-content;
}

.intro-mountains {
    position: relative;

    .left-cloud,
    .right-cloud,
    .middle-cloud {
        position: absolute;
        width: 9.4rem;
        top: 3.4rem;
        filter: blur(2px);
        transform: rotateY(180deg);
    }

    .middle-cloud {
        top: -7rem;
        left: calc(50% - 7rem);
        z-index: -1;
        filter: blur(4px);
    }

    .left-cloud {
        left: calc(50% - 27.4rem);
    }

    .right-cloud {
        transform: rotateY(180deg);
        left: calc(50% + 10.6rem);
    }
}

.intro-mountain {
    margin-top: 1.4rem;
    position: relative;
    z-index: 1;

    @media screen and (max-width: 767px) {
        max-width: 345px;
        display: block;
        margin: 1.4rem auto 0 auto;
    }
}

.grass-bg {
    padding: 78px 0 0 0;
    margin-top: -77px;
    padding-bottom: 150px;
    position: relative;
    z-index: 0;
    color: #fff;
    text-align: center;
    background: #7bbd40 url('https://images.dundle.com/resources/images/loyalty/intro/forest.svg');
    background-position: bottom left;
    background-repeat: repeat-x;
    background-size: auto 150px;

    @media screen and (max-width: 991px) {
        margin-top: -56px;
    }

    @media screen and (max-width: 767px) {
        margin-top: -26px;
    }

    .free-products {
        margin-top: 6rem;
        padding-bottom: 4rem;
    }

    img {
        max-width: 14rem;
    }

    .title {
        font-weight: 700;
        font-size: 1.3333333333333333rem;

        @media screen and (min-width: 768px) {
            font-size: 1.6rem;
        }
    }

    .money-tree {
        width: 8rem;
        position: absolute;
        top: 9rem;
        left: calc(50% - 49rem);

        @media screen and (max-width: 767px) {
            left: -4.6rem;
            top: 34rem;
        }
    }

    .cash-tree {
        width: 10rem;
        position: absolute;
        top: 33rem;
        left: calc(50% - 45rem);
    }

    .treasure-tree {
        width: 14rem;
        position: absolute;
        top: 2.6rem;
        left: calc(50% + 26rem);
    }

    .bush {
        width: 8rem;
        position: absolute;
        top: 24.5rem;
        left: calc(50% + 42rem);

        @media screen and (max-width: 767px) {
            left: initial;
            top: initial;
            bottom: 35rem;
            right: -3.3rem;
        }
    }
}

.faq {
    background: #013700;
    position: relative;
    z-index: 0;
    padding: 5rem 0 6.5rem 0;
    margin-top: -1px;

    @media screen and (max-width: 767px) {
        padding-top: 1rem;
    }

    .faq-accordion {
        color: $color-grey-2;

        ::v-deep .item {
            .accordion-title,
            svg {
                color: #fff;
            }

            .accordion-title {
                padding-right: 0.8rem;
            }
            button {
                &:hover {
                    & > span,
                    svg {
                        color: white !important;
                    }
                }
            }
            h3 button {
                padding-left: 0.8rem;
                text-align: left;
            }

            h3 button[aria-expanded='true'] > span,
            h3 button[aria-expanded='true'] > svg {
                color: #fff !important;
            }

            .content {
                background: rgba(0, 0, 0, 0.2);
            }
        }
    }
}

.start-shopping {
    margin-top: 6.5rem;
    text-align: center;

    .shopping-text {
        color: #fff;
        font-size: 1.6rem;
        font-weight: 700;
        margin-bottom: 2rem;

        @media screen and (max-width: 767px) {
            font-size: 1.3333333333333333rem;
        }
    }

    .start-shopping-button {
        @media screen and (max-width: 767px) {
            width: 100%;
        }

        min-width: 220px;

        @media screen and (min-width: 768px) {
            &::before {
                content: none;
            }
        }
    }
}

@keyframes float {
    0% {
        transform: translatey(0px);
    }
    50% {
        transform: translatey(15px);
    }
    100% {
        transform: translatey(0px);
    }
}

@keyframes float2 {
    0% {
        transform: translatey(0px);
    }
    50% {
        transform: translatey(-15px);
    }
    100% {
        transform: translatey(0px);
    }
}

@keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(-6px);
    }
    60% {
        transform: translateY(-3px);
    }
}
</style>

<i18n src="@dundle/locale/data/loyalty.json"></i18n>
<i18n src="@dundle/locale/data/buttons.json"></i18n>

<i18n></i18n>
